'use client';
import { Fragment, useState } from 'react';

import LinkText from '@/components/common/Link/Link';
import Image from 'next/image';
import cn from 'classnames';

import ChevronDown from 'public/img/icon/chevron-down-black.svg';
import LineDivider from '@/components/common/LineDivider/LineDivider';
import Text from '@/components/common/Text/Text';

import './FAQSection.style.scss';
import RichText from '@/components/common/RichText/RichText';

export type FAQQuestionProps = {
  question: string;
  answer: string;
};

function FAQQuestion(props: FAQQuestionProps) {
  const { question, answer } = props;
  const [isOpen, setOpen] = useState(false);

  const handleExpand = () => {
    setOpen(!isOpen);
  };

  return (
    <div className='FAQQuestion'>
      <div
        className='FAQQuestion-question cursor'
        onClick={handleExpand}
        role='button'
      >
        <Text variant='title-xs'>{question}</Text>
        <Image
          className={cn('FAQQuestion-chevron', { flip: isOpen })}
          src={ChevronDown}
          height={16}
          width={32}
          alt='Chevron down to show anser to FAQ question'
        />
      </div>
      <div className={cn('FAQQuestion-answer', { open: isOpen })}>
        <div className='FAQQuestion-answer-inner'>
          <RichText>{answer}</RichText>
        </div>
      </div>
    </div>
  );
}

type FAQSectionProps = {
  className?: string;
  headline: string;
  questions: FAQQuestionProps[];
  ctaText?: string;
  ctaLink?: string;
};

function FAQSection(props: FAQSectionProps) {
  const { className = '', headline, questions, ctaText, ctaLink } = props;

  return (
    <div className={cn('FAQSection', className)}>
      <Text
        className='FAQSection-headline'
        variant='title-m'
        italic
      >
        {headline}
      </Text>
      <div className='FAQSection-questions'>
        {questions.map((item, index) => {
          return (
            <Fragment key={index}>
              <FAQQuestion
                key={index}
                {...item}
              />
              <LineDivider
                color='gray'
                size='thin'
              />
            </Fragment>
          );
        })}
      </div>
      {ctaText && ctaLink && (
        <div className='FAQSection-cta'>
          <LinkText
            className='FAQSection-cta-link'
            variant='link-m'
            href={ctaLink || '/'}
          >
            {ctaText}
          </LinkText>
        </div>
      )}
    </div>
  );
}

export default FAQSection;
